import {
  useCallback,
  useState,
  MutableRefObject,
  useEffect,
  useRef,
} from 'react';

import {
  FormLogEvent,
  FormEventsState,
} from '@monoimin/forms/gravity-form/types';
import { logFormEvent } from '@monoimin/forms/gravity-form/utils';
import { Locales, FormInputs, FieldInteraction } from '@monoimin/sb-types';

import { getAnalyticsTrackingId, getIpData } from '@monoimin/utils/analytics';
import { getDeviceInfo } from 'libs/analytics/src/lib/amplitude';
import { generateEventId } from 'libs/analytics/src/lib/utils/generateEventId';
import { getSessionStartTime } from 'libs/utils/src/lib/storage/sessionStorage';
import { defaultFormEventsState } from 'libs/forms/src/gravity-form/utils/constants';
import { createCountryInfo } from 'libs/analytics/src/lib/utils/createCountryInfo';
import { trackAmplitudeEvent } from 'libs/analytics/src/lib/amplitude/amplitude';

import { useIntersectionObserver } from '../useIntersectionObserver/useIntersectionObserver';

const initFormEventsState: FormEventsState = {
  ...defaultFormEventsState,
};

export type AnalyticsData = {
  programStatus?: string;
  formUiElement?: 'inline' | 'popup' | 'new_window';
  programRelation?: boolean;
  formType?: string;
  formId?: number;
  pageCountry?: string | string[];
};

export const FORM_SUBMITTED_SUCCESS = 'Form submitted success';

type UseFormAnalyticsArgs = {
  ref: MutableRefObject<HTMLFormElement | null>;
  formTitle: string;
  locale: Locales;
  formValues: FormInputs;
  analyticsData?: AnalyticsData;
  fieldInteractions: FieldInteraction[];
};

export const useFormAnalytics = ({
  ref,
  formTitle,
  locale,
  formValues,
  analyticsData,
  fieldInteractions,
}: UseFormAnalyticsArgs) => {
  // объект состояния для событий формы, по умолчанию они все в статусе 'waiting', если событие произошло, то статус меняется на 'pending', если запрос отправлен, то на 'complete' или 'error'
  const [formEvents, setFormEvents] =
    useState<FormEventsState>(initFormEventsState);

  const lastSentDataRef = useRef<Record<string, any> | null>(null);

  const entry = useIntersectionObserver(ref, {
    rootMargin: '-65px 0px 0px 0px',
  });

  const isFormIntersecting = !!entry?.isIntersecting;

  // Function to compare objects and check if there are meaningful differences
  const hasDataChanged = (newData: Record<string, any>): boolean => {
    const oldData = lastSentDataRef.current;

    // Only compare these keys for deduplication
    const significantKeys = [
      'form_mtg_type',
      'user_email',
      'user_name',
      'form_meeting_manager',
      'form_question',
      'form_meeting_platform',
      'form_user_goal',
      'form_meeting_time',
      'form_meeting_date',
      'form_meeting_location',
      'form_user_client_segment',
      'form_action',
      'page_submission_flag',
      'form_fields_errors_count',
      'form_fields_filled_count',
      'form_fields_interactions',
    ];

    if (!oldData) {
      // Save only the significant fields for future comparison
      lastSentDataRef.current = Object.fromEntries(
        significantKeys.map((key) => [key, newData[key]])
      );
      return true;
    }

    for (const key of significantKeys) {
      if (
        !Object.prototype.hasOwnProperty.call(newData, key) ||
        !Object.prototype.hasOwnProperty.call(oldData, key)
      ) {
        continue;
      }

      if (key === 'form_fields_interactions') {
        if (newData[key]?.length !== oldData[key]?.length) {
          lastSentDataRef.current = Object.fromEntries(
            significantKeys.map((k) => [k, newData[k]])
          );
          return true;
        }
        for (let i = 0; i < newData[key]?.length; i++) {
          const newField = newData[key][i];
          const oldField = oldData[key][i];
          if (
            newField.form_field_error_flag !== oldField.form_field_error_flag ||
            newField.form_field_length !== oldField.form_field_length ||
            newField.form_field_name !== oldField.form_field_name ||
            newField.form_field_focus_flag !== oldField.form_field_focus_flag
          ) {
            lastSentDataRef.current = Object.fromEntries(
              significantKeys.map((k) => [k, newData[k]])
            );
            return true;
          }
        }
      } else if (
        JSON.stringify(newData[key]) !== JSON.stringify(oldData[key])
      ) {
        lastSentDataRef.current = Object.fromEntries(
          significantKeys.map((k) => [k, newData[k]])
        );
        return true;
      }
    }

    // No significant changes
    return false;
  };

  const onFormLog = useCallback(
    async (event: FormLogEvent | null, rentaResponse?: string) => {
      if (!event) {
        return;
      }

      // Force logging for critical error events
      const isCriticalError =
        event.includes('failed') || event.includes('error');

      if (formEvents[event] === 'waiting') {
        const prevState = { ...formEvents };

        setFormEvents({ ...prevState, [event]: 'pending' });

        const ipData = await getIpData();
        const trackingId = getAnalyticsTrackingId() || '';
        const { deviceId, sessionId } = getDeviceInfo();

        const { region: programRegion } = await createCountryInfo({
          pageCountry: analyticsData?.pageCountry,
        });

        const currentDate = new Date();

        const formInteractiveLogData = {
          form_mtg_type: (formValues['form_mtg_type'] as string) || '',
          device_city_ip: ipData.ipcity,
          device_country_ip: ipData.ipcountry,
          device_ga_cookie_id: trackingId,
          partner_comment: (formValues['partner_comment'] as string) || '',
          user_questions: (formValues['user_questions'] as string) || '',
          device_id: deviceId,
          page_previous_url: document.referrer || '',
          session_started_at: getSessionStartTime(),
          program_region: Array.isArray(programRegion)
            ? programRegion.join(',')
            : programRegion || '',
          page_previous_title: '',
          page_title: document.title || '',
          page_url: window.location.href,
          event_id: generateEventId('form', {
            formId: analyticsData?.formId,
            action: event,
            timestamp: currentDate.toISOString(),
          }),
          program_url: '',
          event_datetime: currentDate.toISOString(),
          device_timezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone || '',
          program_name: (formValues['program_name'] as string) || '',
          program_flag: analyticsData?.programRelation,
          event_date: currentDate.toISOString().split('T')[0],
          session_id: sessionId,
          page_type: (formValues['sf_lead_f_type'] as string) || '',
          site_hostname: window.location.hostname,
          page_location: window.location.href,
          page_path: window.location.pathname,
          device_datetime: currentDate.toISOString(),
          form_fields_interactions: fieldInteractions,
          form_fields_errors_count: fieldInteractions?.filter(
            (field) => field.form_field_error_flag === 1
          ).length,
          program_status: analyticsData?.programStatus,
          form_fields_filled_count: fieldInteractions?.filter(
            (field) => field.form_field_length > 0
          ).length,
          form_action: event,
          form_meeting_manager:
            (formValues['form_meeting_manager'] as string) || '',
          form_question: (formValues['form_question'] as string) || '',
          form_meeting_platform:
            (formValues['form_meeting_platform'] as string) || '',
          form_user_goal: (formValues['form_user_goal'] as string) || '',
          form_meeting_time: (formValues['form_meeting_time'] as string) || '',
          form_meeting_date: (formValues['form_meeting_date'] as string) || '',
          form_meeting_location:
            (formValues['form_meeting_location'] as string) || '',
          form_id: analyticsData?.formId,
          form_user_client_segment:
            (formValues['form_user_client_segment'] as string) || '',
          form_ui_element: analyticsData?.formUiElement || '',
          form_name: formTitle,
          form_type: analyticsData?.formType,
          page_submission_flag: event === FORM_SUBMITTED_SUCCESS,
          page_language: locale,
          event_type: 'form_interaction',
          site_domain: window.location.hostname,
        };

        const dataHasChanged =
          isCriticalError || hasDataChanged(formInteractiveLogData);

        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
        }

        const sanitizedFormInteractiveLogData = Object.fromEntries(
          Object.entries(formInteractiveLogData).map(([key, value]) => {
            if (value === '') {
              return [key, null];
            }
            return [key, value];
          })
        );

        await trackAmplitudeEvent(
          {
            deviceId,
            userId: null,
            name: 'Form interactive',
            properties: {
              ...sanitizedFormInteractiveLogData,
              form_event: event,
              form_language: locale,
              form_title: formTitle,
            },
          },
          false,
          true
        );

        if (dataHasChanged) {
          // отправляем лог в ренту
          if (window.renta?.track) {
            window.renta.track('Form interactive', formInteractiveLogData);
          } else {
            setTimeout(
              () =>
                window.renta?.track('Form interactive', formInteractiveLogData),
              1000
            );
          }

          const sanitizedFormInteractiveLogData = Object.fromEntries(
            Object.entries(formInteractiveLogData).map(([key, value]) => {
              if (value === '') {
                return [key, null];
              }
              return [key, value];
            })
          );

          const dataLayerCompatibleData = {
            ...sanitizedFormInteractiveLogData,
            event: 'form_interaction',
            form_fields_interactions: JSON.stringify(
              formInteractiveLogData.form_fields_interactions
            ),
          };

          window.dataLayer.push(dataLayerCompatibleData);

          const filteredEmptyFormValues = Object.entries(formValues).reduce<
            Record<string, string>
          >((acc, [key, value]) => {
            if (value) {
              return { ...acc, [key]: String(value) };
            }
            return acc;
          }, {});

          // отправляем лог в newRelic через роут /api/log
          const res = await logFormEvent({
            userAction: event,
            locale,
            rentaResponse,
            formId: formValues['fr_form_id'] as string,
            formTitle: formTitle,
            filteredFormValues: filteredEmptyFormValues,
            formLogData: formInteractiveLogData,
          });

          setFormEvents({
            ...prevState,
            [event]: res?.requestId ? 'complete' : 'error',
          });
        }
      }
    },
    [formEvents, formTitle, locale, formValues, fieldInteractions]
  );

  useEffect(() => {
    if (formEvents['Form loaded but invisible'] === 'waiting') {
      onFormLog('Form loaded but invisible');
    }
  }, [onFormLog, formEvents]);

  useEffect(() => {
    if (isFormIntersecting && formEvents['Form visible'] === 'waiting') {
      onFormLog('Form visible');
    }
  }, [isFormIntersecting, onFormLog, formEvents]);

  return {
    formEvents,
    onFormLog,
  };
};
